import { useEffect, useMemo, useState } from "react";
import { geolocation } from "@/shared/geo/geolocationStore";
import { useAgentPositionFrom } from "../../tracking/hooks/useAgentPosition";
import { useTripLiveUpdates } from "@/shared/realtime";
import { useLiveRoute } from "../../../shared/geo";
import { isTripFailed, isTripFinal } from "../../../shared/format";
import type { Coordinate } from "../../../entities/tracking";
import type { Trip } from "../../../entities/trip";

// Données de la carte de suivi LIVE d'une course multi-arrêts, partagées entre
// l'encart carte du suivi (ClientTripTrackingScreen) et l'écran carte plein
// écran (TripLiveMapScreen) :
//   - position GPS live de l'agent via socket `agent.location.updated` ;
//   - géométrie FIXE du trajet (arrêts ordonnés + livraison) — stable, donc
//     l'itinéraire routier n'est calculé qu'une fois (cache TanStack) ;
//   - position de l'appareil du client (pour le situer sur la carte).
// `mapData` reste null tant qu'aucune position agent n'est reçue.
export function useTripLiveMap(trip: Trip | undefined) {
  // La course est-elle encore vivante ? Inutile de traquer une Main sur une
  // course annulee ou livree — et inutile, surtout, de faire tourner le repli.
  const isTripLive = Boolean(
    trip?.status && !isTripFailed(trip.status) && !isTripFinal(trip.status),
  );

  // La position vient de la salle de la COURSE (R7/R8), plus de celle de
  // l'agent. Deux defauts disparaissent avec ce changement :
  //
  //   • il fallait connaitre l'identifiant de la Main pour ecouter — inconnu
  //     tant qu'aucune n'est affectee, alors que la carte s'ouvre des la
  //     creation de la course ;
  //   • le client restait dans `agent:{id}` APRES la fin de la course, et
  //     continuait de recevoir les positions de cette personne sur ses autres
  //     missions. La salle d'une course, elle, se vide d'elle-meme.
  //
  // Le repli REST demeure : socket coupe, la carte ne doit pas rester vide
  // quand le backend connait parfaitement la position.
  const liveLoc0 = useTripLiveUpdates(isTripLive ? trip?.id : undefined);
  const agentPosition = useAgentPositionFrom(
    liveLoc0,
    trip?.agent_id ?? undefined,
    isTripLive,
  );
  const liveLoc = agentPosition.coord;
  const isLiveAgent = Boolean(liveLoc) && isTripLive;

  // Position du client : passe par le store partage, qui a deja l'autorisation
  // et, le plus souvent, une position en cache. Cet ecran redemandait la
  // permission pour son propre compte — une invite de plus et un allumage du
  // GPS de plus, alors que l'app la detient depuis l'ecran d'accueil.
  const [userLocation, setUserLocation] = useState<Coordinate | null>(null);
  useEffect(() => {
    let cancelled = false;
    void geolocation.ensurePosition().then((coord) => {
      if (!cancelled && coord) setUserLocation(coord);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const geo = useMemo(() => {
    if (!trip) return null;
    const stopCoords = [...(trip.stops ?? [])]
      .sort((a, b) => (a.stop_index ?? 0) - (b.stop_index ?? 0))
      .map((s) => s.location)
      .filter((l): l is { lat: number; lng: number; address?: string | null } =>
        l?.lat != null && l?.lng != null)
      .map((l) => ({ latitude: l.lat, longitude: l.lng }));
    const addr = trip.delivery_address;
    const dropoffLocation: Coordinate | null =
      addr?.latitude != null && addr?.longitude != null
        ? { latitude: addr.latitude, longitude: addr.longitude }
        : stopCoords[stopCoords.length - 1] ?? null;
    const activeIdx = trip.current_stop_index ?? 0;
    return { stopCoords, dropoffLocation, activeIdx };
  }, [trip]);

  // ─── Itinéraire routier, calculé DEPUIS LA MAIN ────────────────────────────
  //
  // La mécanique elle-même (origine figée, verrou anti-rafale, recalcul sur
  // écart) vit maintenant dans `useLiveRoute` : le suivi des COMMANDES et
  // celui des URGENCES en avaient exactement le même besoin et traçaient, faute
  // de l'avoir, une ligne droite. La règle est une, elle n'a pas à être
  // réécrite par écran.
  //
  // Les arrêts DÉJÀ traités ne font plus partie du trajet à parcourir : les
  // garder ferait revenir la ligne sur ses pas.
  const destinations = useMemo(() => {
    if (!geo) return [];
    const remaining = geo.stopCoords.slice(geo.activeIdx);
    return geo.dropoffLocation ? [...remaining, geo.dropoffLocation] : remaining;
  }, [geo]);

  const { routePoints: liveRoutePoints } = useLiveRoute(liveLoc, destinations, isTripLive);

  const mapData = useMemo(() => {
    if (!trip || !liveLoc || !geo) return null;
    const agentLocation: Coordinate = { latitude: liveLoc.latitude, longitude: liveLoc.longitude };
    const dropoffLocation = geo.dropoffLocation ?? agentLocation;
    const pickupLocation =
      geo.stopCoords[geo.activeIdx] ?? geo.stopCoords[0] ?? dropoffLocation;
    // `useLiveRoute` rend déjà le repli ligne droite quand aucune route n'a pu
    // être calculée — on n'en refait pas un ici.
    const routePoints =
      liveRoutePoints.length > 1 ? liveRoutePoints : [agentLocation, dropoffLocation];
    return { agentLocation, pickupLocation, dropoffLocation, routePoints };
  }, [trip, liveLoc, geo, liveRoutePoints]);

  return {
    liveLoc,
    isLiveAgent,
    mapData,
    userLocation,
    // Quand la position affichée a été prise. C'est elle qui permet à l'écran
    // de la DATER au lieu de la présenter comme du direct — cf.
    // `tracking/lib/positionFreshness`.
    positionUpdatedAt: agentPosition.updatedAt,
  };
}
